import React, { useCallback, useEffect, useMemo, useState } from "react";
import { preOrdersAPI, shopsAPI } from "../services/api";
import { useI18n } from "../i18n";
import { useShopEvent } from "../hooks/useShopEvent";
import OrderAlerts from "../components/OrderAlerts";
import { formatMoney } from "../pos/utils/money";
import {
  daysFromToday,
  formatClock,
  formatDay,
  formatTime,
  relativeDay,
} from "../utils/dates";
import { useMembership } from "../context/MembershipContext";
import EmptyState from "../components/EmptyState";

/**
 * Reservations customers made from the menu.
 *
 * Read on a phone at the counter, so each order answers the questions in the
 * order they get asked: who is it for, when are they coming, and what goes
 * aside. Dates are words — "Tomorrow · Thu 10 Sep at 17:00" — never the raw
 * value the API sends.
 *
 * The list follows the shop's live connection, so a new reservation appears
 * here without anyone refreshing.
 */
const STATUS_STYLES = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-300",
  ready: "bg-blue-100 text-blue-800 border-blue-300",
  completed: "bg-green-100 text-green-800 border-green-300",
  cancelled: "bg-gray-100 text-gray-600 border-gray-300",
};

const STATUSES = ["pending", "ready", "completed", "cancelled"];

/** Still to be collected: these go first, soonest pickup at the top. */
const isOpen = (order) =>
  order.status === "pending" || order.status === "ready";

const pickupKey = (order) => `${order.pickup_date} ${order.pickup_time}`;

const PreOrders = () => {
  const { t } = useI18n();
  const [preOrders, setPreOrders] = useState([]);
  const [currency, setCurrency] = useState("TND");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updating, setUpdating] = useState(null);

  const fetchPreOrders = useCallback(async () => {
    try {
      const response = await preOrdersAPI.getAll();
      setPreOrders(response.data);
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || t("preorders.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchPreOrders();

    shopsAPI
      .current()
      .then(({ data }) => data.shop?.currency && setCurrency(data.shop.currency))
      .catch(() => {});
  }, [fetchPreOrders]);

  // Coming back to the app — often from a "new pre-order" notification —
  // should show that order without anyone having to pull to refresh
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") fetchPreOrders();
    };

    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [fetchPreOrders]);

  useShopEvent("preorder:created", fetchPreOrders);
  useShopEvent("preorder:updated", fetchPreOrders);
  useShopEvent("connect", fetchPreOrders);

  // paymentMethod is only sent when the order is being handed over
  const handleStatusChange = async (id, status, paymentMethod) => {
    setUpdating(id);
    setError("");

    try {
      await preOrdersAPI.updateStatus(id, status, paymentMethod);
      await fetchPreOrders();
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          t("preorders.updateFailed"),
      );
    } finally {
      setUpdating(null);
    }
  };

  const sorted = useMemo(
    () =>
      [...preOrders].sort((a, b) => {
        if (isOpen(a) !== isOpen(b)) return isOpen(a) ? -1 : 1;

        // Open orders soonest first; finished ones most recent first
        return isOpen(a)
          ? pickupKey(a).localeCompare(pickupKey(b))
          : pickupKey(b).localeCompare(pickupKey(a));
      }),
    [preOrders],
  );

  const waiting = sorted.filter(isOpen).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-1">
          {t("preorders.title")}
        </h1>
        <p className="text-gray-600">
          {waiting === 0
            ? t("preorders.nothingWaiting")
            : t("preorders.waiting", { count: waiting })}
        </p>
      </div>

      <OrderAlerts />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl px-4 py-3">
          {error}
        </div>
      )}

      {sorted.length === 0 ? (
        <EmptyState screen="preorders" message={t("preorders.empty")} />
      ) : (
        <div className="grid gap-4 sm:gap-6">
          {sorted.map((order) => (
            <PreOrderCard
              key={order.id}
              order={order}
              currency={currency}
              busy={updating === order.id}
              onStatus={(status, paymentMethod) =>
                handleStatusChange(order.id, status, paymentMethod)
              }
            />
          ))}
        </div>
      )}
    </div>
  );
};

const PAYMENT_METHODS = ["cash", "card", "mobile_payment"];

const PreOrderCard = ({ order, currency, busy, onStatus }) => {
  const { t } = useI18n();
  const { can } = useMembership();
  // Completing an order records its sale, so it asks how it was paid first
  const [handingOver, setHandingOver] = useState(false);
  const [method, setMethod] = useState("cash");
  const items = order.items || [];
  const saving =
    Math.round(
      items.reduce((sum, item) => sum + (Number(item.offer_discount) || 0), 0) *
        100,
    ) / 100;

  const chooseStatus = (status) => {
    if (status === "completed") {
      setMethod("cash");
      setHandingOver(true);
      return;
    }

    onStatus(status);
  };

  const handOver = async () => {
    await onStatus("completed", method);
    setHandingOver(false);
  };

  return (
    <article
      className={`bg-white rounded-xl shadow-lg p-4 sm:p-6 ${
        isOpen(order) ? "" : "opacity-75"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-lg font-bold text-gray-800 truncate">
            {order.user_name || t("preorders.customer")}
          </h3>
          {order.user_phone && (
            <a
              href={`tel:${order.user_phone}`}
              dir="ltr"
              className="text-sm text-primary-600 hover:underline"
            >
              {order.user_phone}
            </a>
          )}
        </div>

        <select
          value={order.status}
          // A handed-over order has a sale behind it: it is refunded from
          // Purchases, never reopened here
          // and someone only given to see pre-orders cannot move them
          disabled={
            busy || order.status === "completed" || !can("preorders.edit")
          }
          onChange={(e) => chooseStatus(e.target.value)}
          aria-label={t("preorders.statusAria")}
          className={`shrink-0 px-3 py-2 rounded-lg border font-medium disabled:opacity-60 ${
            STATUS_STYLES[order.status] || STATUS_STYLES.cancelled
          }`}
        >
          {STATUSES.map((status) => (
            <option key={status} value={status}>
              {t(`preorders.status.${status}`)}
            </option>
          ))}
        </select>
      </div>

      {handingOver && (
        <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 space-y-2">
          <p className="text-sm font-medium text-emerald-900">
            {t("pos.pickups.paidBy")}
          </p>
          <div
            role="group"
            aria-label={t("pos.pickups.paidBy")}
            className="grid grid-cols-3 gap-2"
          >
            {PAYMENT_METHODS.map((id) => (
              <button
                key={id}
                type="button"
                onClick={() => setMethod(id)}
                aria-pressed={method === id}
                className={`h-10 rounded-lg border text-sm font-semibold ${
                  method === id
                    ? "bg-emerald-600 border-emerald-600 text-white"
                    : "bg-white border-gray-300 text-gray-700"
                }`}
              >
                {t(`common.payment.${id}`)}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="flex-1 text-sm text-emerald-900">
              {t("pos.pickups.confirm", {
                amount: formatMoney(order.total_amount, currency),
              })}
            </span>
            <button
              type="button"
              onClick={handOver}
              disabled={busy}
              className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold disabled:opacity-60"
            >
              {busy ? "…" : t("pos.pickups.handedOver")}
            </button>
            <button
              type="button"
              onClick={() => setHandingOver(false)}
              className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm"
            >
              {t("pos.pickups.notYet")}
            </button>
          </div>
        </div>
      )}

      <PickupWhen order={order} />

      {items.length > 0 && (
        <ul className="mt-3 divide-y divide-gray-100">
          {items.map((item) => (
            <ItemLine key={item.id} item={item} currency={currency} />
          ))}
        </ul>
      )}

      {order.notes && (
        <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
          “{order.notes}”
        </p>
      )}

      <div className="mt-3 pt-3 border-t border-gray-100 flex items-end justify-between gap-3">
        <p className="text-xs text-gray-500">{placedLabel(order.created_at, t)}</p>
        <div className="text-end">
          {saving > 0 && (
            <p className="text-xs font-medium text-amber-700">
              {t("preorders.offersSaving", {
                amount: formatMoney(saving, currency),
              })}
            </p>
          )}
          <p className="text-2xl font-bold text-primary-600">
            {formatMoney(order.total_amount, currency)}
          </p>
        </div>
      </div>
    </article>
  );
};

/** When they are coming, in words, and whether they are late. */
const PickupWhen = ({ order }) => {
  const { t } = useI18n();
  const diff = daysFromToday(order.pickup_date);
  const open = isOpen(order);
  const late = open && diff !== null && diff < 0;
  const soon = open && (diff === 0 || diff === 1);

  const relative = relativeDay(order.pickup_date);
  const date = formatDay(order.pickup_date);
  const day = relative === date ? date : `${relative} · ${date}`;

  const tone = late
    ? "bg-red-50 text-red-800"
    : soon
      ? "bg-blue-50 text-blue-900"
      : "bg-gray-50 text-gray-700";

  return (
    <div className={`mt-3 flex items-center gap-3 rounded-lg px-3 py-2 ${tone}`}>
      <span className="text-xl" aria-hidden="true">
        📅
      </span>
      <div>
        <p className="font-semibold">
          {t("preorders.pickupAt", {
            day,
            time: formatTime(order.pickup_time),
          })}
        </p>
        {late && (
          <p className="text-xs">
            {t("preorders.late", { count: Math.abs(diff) })}
          </p>
        )}
      </div>
    </div>
  );
};

/** One line, with the offer that lowered it and the price it would have been. */
const ItemLine = ({ item, currency }) => {
  const { t } = useI18n();
  const saving = Number(item.offer_discount) || 0;
  const gross = Number(item.unit_price) * Number(item.quantity);

  return (
    <li className="py-2 flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-gray-800">
          <span className="font-semibold">{item.quantity} ×</span>{" "}
          {item.product_name}
        </p>
        {saving > 0 && (
          <p className="text-xs font-medium text-amber-700">
            🏷️ {item.offer_title || t("preorders.offerFallback")} · −
            {formatMoney(saving, currency)}
          </p>
        )}
      </div>
      <div className="text-end shrink-0">
        <p className="font-medium text-gray-800">
          {formatMoney(item.subtotal, currency)}
        </p>
        {saving > 0 && (
          <p className="text-xs text-gray-400 line-through">
            {formatMoney(gross, currency)}
          </p>
        )}
      </div>
    </li>
  );
};

const placedLabel = (createdAt, t) => {
  if (!createdAt) return "";

  const diff = daysFromToday(createdAt);
  const time = formatClock(createdAt);

  if (diff === 0) return t("preorders.placedToday", { time });
  if (diff === -1) return t("preorders.placedYesterday", { time });
  return t("preorders.placedOn", { day: formatDay(createdAt), time });
};

export default PreOrders;
